import { LOOT } from '@/config/loot';
import { SECTORS } from '@/config/sectors';
import {
  bestTime, isEndlessUnlocked, isSectorUnlocked, recordSectorClear, recordSectorDeath,
  startWeapon, unlockedSectorCount,
} from '@/core/meta';
import { updateRecords } from '@/core/records';
import { clearSave, getActiveSlot } from '@/core/save';
import { t } from '@/i18n';
import type { World } from '@/scene/world';
import type { Hud } from '@/ui/hud';
import type { RunEndData, RunEndScreen } from '@/ui/run-end-screen';
import type { TitleScreen } from '@/ui/title-screen';
import type { UpgradeScreen } from '@/ui/upgrade-screen';
import { music } from '@/audio/music';
import type { AchievementTracker } from './achievements';
import type { Rewards } from './rewards';
import type { RunFlow } from './run-flow';
import type { RunState } from './run-state';
import { sectorRoomCount } from './sector-hooks';
import type { ScreenSize } from './shop-flow';

/** Seconds between the finale kill and the victory screen (the death blast) */
const VICTORY_DELAY = 2.6;

/** Overlays the sector flow drives. */
export interface SectorScreens {
  readonly runEnd: RunEndScreen;
  readonly shop: UpgradeScreen;
  readonly title: TitleScreen;
}

/**
 * Sector run end (EPIC D3, docs/01): the finale kill → VICTORY (meta clear,
 * best time, unlocks, achievements, pilot XP); a death → the sector is
 * LOST (roguelike — no checkpoint, no tax). Either way the slot's run is
 * over and its save is cleared; the next run is always a fresh build.
 */
export class SectorFlow {
  /** Fight time frozen at the finale kill (the blast delay doesn't count) */
  private clearTime = 0;
  /** The run end on screen (drives the primary button) */
  private result: RunEndData | null = null;

  constructor(
    private readonly world: World,
    private readonly run: RunState,
    private readonly flow: RunFlow,
    private readonly rewards: Rewards,
    private readonly achievements: AchievementTracker,
    private readonly screens: SectorScreens,
    private readonly hud: Hud,
    private readonly view: ScreenSize,
  ) {}

  /** The finale fell: let the blast play, then the victory screen. */
  onFinaleWon(): void {
    // The ship fell on the same step: the loss already owns the screen
    if (this.screens.runEnd.open) return;
    this.clearTime = this.run.stats.timeAlive;
    this.run.victoryTimer = VICTORY_DELAY;
    this.hud.notify(t('victory.title'), 'portal');
  }

  /** Fight-step clock (game.ts): counts the victory delay down. */
  tick(dt: number): void {
    const run = this.run;
    if (run.victoryTimer <= 0) return;
    run.victoryTimer -= dt;
    if (run.victoryTimer <= 0) this.showVictory();
  }

  private showVictory(): void {
    const { run, world } = this;
    const index = run.sectorIndex;
    run.victoryTimer = 0;
    run.stats.bulletsFired = world.bullets.friendlyFired;
    const openBefore = unlockedSectorCount();
    const endlessBefore = isEndlessUnlocked();
    const best = bestTime(index);
    this.achievements.onSectorCleared(this.clearTime);
    const newBest = recordSectorClear(index, this.clearTime);
    const xp = LOOT.sectorClearXp[index] ?? 0;
    this.rewards.addXp(xp);
    clearSave(); // the run is won and over — the slot frees up
    let unlock: RunEndData['unlock'] = null;
    if (unlockedSectorCount() > openBefore) unlock = { kind: 'sector', index: index + 1 };
    else if (!endlessBefore && isEndlessUnlocked()) unlock = { kind: 'endless' };
    this.open({
      ...this.base('victory'),
      clearTime: this.clearTime,
      newBest: newBest && best !== null,
      best,
      unlock,
      xp,
      hasNext: index + 1 < SECTORS.length && isSectorUnlocked(index + 1),
    });
  }

  /** Death in a sector run (DeathFlow → here): the sector is lost. */
  onDeath(): void {
    const { run, world } = this;
    run.stats.bulletsFired = world.bullets.friendlyFired;
    recordSectorDeath(run.sectorIndex);
    clearSave();
    this.open(this.base('defeat'));
  }

  private base(kind: RunEndData['kind']): RunEndData {
    const run = this.run;
    return {
      kind,
      sector: run.sectorIndex,
      stats: run.stats,
      improved: updateRecords(run.stats),
      seed: run.seed,
      totalRuns: run.totalRuns,
      earned: [...run.earned],
      clearTime: 0, newBest: false, best: null, unlock: null, xp: 0, hasNext: false,
      room: run.roomInSector,
      rooms: sectorRoomCount(run),
    };
  }

  private open(data: RunEndData): void {
    this.result = data;
    music.playShop(); // calm garage loop under the summary
    this.screens.runEnd.show(this.view.width, this.view.height, data);
  }

  /**
   * Primary button: NEXT SECTOR / PLAY AGAIN after a win, RETRY SECTOR after
   * a loss — always a fresh build in the same slot (seed pin kept).
   */
  primary(): void {
    const result = this.result;
    if (!result) return;
    const run = this.run;
    const win = result.kind === 'victory';
    const sector = win && result.hasNext ? result.sector + 1 : result.sector;
    const tries = win ? 1 : run.totalRuns + 1;
    const last = this.flow.lastSetup;
    this.screens.runEnd.hide();
    this.result = null;
    this.flow.startRun(getActiveSlot(), {
      mode: 'sector', sector, seed: last?.seed ?? null, weapon: last?.weapon ?? startWeapon(),
    });
    run.totalRuns = tries; // a retry keeps counting attempts in this slot
  }

  menu(): void {
    this.result = null;
    this.screens.runEnd.hide();
    this.screens.shop.hide();
    this.screens.title.show(this.view.width, this.view.height);
  }
}
